import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DollarSign, TicketPlus, Trash2, StarIcon } from "lucide-react";
import { ProBadge } from "@/components/ui/pro-badge";
import UpgradePrompt from "@/components/subscription/UpgradePrompt";
// import { set } from "date-fns";
// import { EventDataResponse } from "@/pages/CreateEvent";
import { useToast } from "@/hooks/use-toast";

export interface TicketType {
  id?: string;
  name?: string;
  ticket_name: string;
  description: string;
  price: number;
  max_quantity?: number;
  sale_start_date?: string;
  sale_end_date?: string;
}

interface TicketTypeManagerProps {
  ticketTypes: TicketType[];
  onTicketTypesChange: (ticketTypes: TicketType[]) => void;
  onUpgradeClick?: (featureName?: string) => void;
  accountType?: {
    account_type: string;
    status: string;
    valid_until: null | string;
  };
}

const TicketTypeManager: React.FC<TicketTypeManagerProps> = ({
  ticketTypes,
  onTicketTypesChange,
  onUpgradeClick,
  accountType,
}) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTicket, setNewTicket] = useState<Partial<TicketType>>({
    ticket_name: "",
    description: "",
    price: 0,
  });
  const { toast } = useToast();

  const [maxAttendeesError, setMaxAttendeesError] = useState(""); // New state for max attendees error

  useEffect(() => {
    if (
      ticketTypes &&
      ticketTypes.length >= 1 &&
      accountType?.account_type === "standard"
    ) {
      setError("Standard users are limited to one ticket type.");
    } else {
      setError(null);
    }
  }, [ticketTypes]);

  const handleAddTicket = () => {
    if (!newTicket.ticket_name) return;
    if (maxAttendeesError) {
      toast({
        title: "Invalid Quantity",
        description: maxAttendeesError,
        variant: "destructive",
      });
      return;
    }

    if (
      ticketTypes &&
      accountType?.account_type === "standard" &&
      ticketTypes.length >= 1
    ) {
      setError("Standard users are limited to one ticket type.");
      return;
    }

    const ticketToAdd: TicketType = {
      ticket_name: newTicket.ticket_name,
      description: newTicket.description,
      price: newTicket.price,
      max_quantity: newTicket.max_quantity,
      sale_start_date: newTicket.sale_start_date,
      sale_end_date: newTicket.sale_end_date,
    };

    onTicketTypesChange([...(ticketTypes ?? []), ticketToAdd]);

    // onTicketTypesChange([...ticketTypes, ticketToAdd]);
    // setNewTicket({
    //   ticket_name: "",
    //   description: "",
    //   price: 0,
    // });
    setIsAddDialogOpen(false);
  };

  const handleDeleteTicket = (id: string) => {
    onTicketTypesChange(
      ticketTypes.filter((ticket) => ticket.ticket_name !== id)
    );
  };

  if (accountType?.account_type === "standard") {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center">
              Multiple Ticket Types <ProBadge className="ml-2" />
            </h3>
            <p className="text-sm text-gray-500">
              Create different ticket types for your event
            </p>
          </div>
        </div>
        <UpgradePrompt
          compact
          onUpgradeClick={onUpgradeClick}
          featuresList={[
            "Create multiple ticket types (Early Bird, VIP, etc.)",
            "Set different prices for each ticket type",
            "Limit availability by quantity or date",
          ]}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border  border-cecure-100 p-6 rounded-[6px]">
        <div>
          <h3 className="text-lg font-semibold flex items-center">
            Multiple Ticket Types 
          </h3>
          <p className="text-sm text-gray-500">
            Create different ticket types for your event
          </p>
        </div>
        <div className="flex items-center rounded-xl px-2 py-[6px] bg-[#FEF8E2]">
               <p className=" text-[#CD7B2E] text-[14px]">Coming soon        
          </p>
          <StarIcon className="ml-2 " size="16" color="#CD7B2E"/>
        </div>
   
        {/* <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button type="button">
              <TicketPlus className="h-4 w-4 mr-2" />
              Add Ticket Type
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Ticket Type</DialogTitle>
              <DialogDescription>
                Create a new ticket type for your event
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Ticket Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Early Bird, VIP, Regular"
                  value={newTicket.ticket_name}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, ticket_name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what's included with this ticket type"
                  value={newTicket.description}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, description: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price ($)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                  <Input
                    id="price"
                    type="number"
                    min="1"
                    step="0.01"
                    className="pl-10"
                    placeholder="0.00"
                    value={newTicket.price}
                    onChange={(e) =>
                      setNewTicket({
                        ...newTicket,
                        price: parseFloat(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div className="flex flex-col ">
                <div className="space-y-2">
                  <Label htmlFor="maxQuantity">Max Quantity (Optional)</Label>
                  <Input
                    id="maxQuantity"
                    type="number"
                    max="100"
                    placeholder="e.g., 100"
                    value={newTicket.max_quantity || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numericValue = parseInt(value, 10);
                      setMaxAttendeesError("");

                      if (!isNaN(numericValue)) {
                        if (
                          numericValue > 100 &&
                          accountType?.account_type === "standard"
                        ) {
                          setMaxAttendeesError(
                            "Limit is reached to add more participants. Go Pro!"
                          );
                        }
                        setNewTicket({
                          ...newTicket,
                          max_quantity: numericValue,
                        });
                      } else {
                        setNewTicket({
                          ...newTicket,
                          max_quantity: undefined,
                        });
                      }
                    }}
                  />
                </div>
                {maxAttendeesError && (
                  <p className="text-red-500 text-sm mt-1">
                    {maxAttendeesError}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="saleStartDate">
                    Sale Start Date (Optional)
                  </Label>
                  <Input
                    id="saleStartDate"
                    type="date"
                    value={newTicket.sale_start_date || ""}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) =>
                      setNewTicket({
                        ...newTicket,
                        sale_start_date: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="saleEndDate">Sale End Date (Optional)</Label>
                  <Input
                    id="saleEndDate"
                    type="date"
                    value={newTicket.sale_end_date || ""}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) =>
                      setNewTicket({
                        ...newTicket,
                        sale_end_date: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setNewTicket({});
                }}
                type="button"
              >
                Cancel
              </Button>
              <Button onClick={handleAddTicket} type="button">
                Add Ticket Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog> */}
      </div>

      {ticketTypes && ticketTypes.length > 0 ? (
        <Card>
          <CardContent className="p-0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ticketTypes &&
                  ticketTypes.map((ticket) => (
                    <TableRow key={ticket.ticket_name}>
                      <TableCell className="font-medium">
                        {ticket.ticket_name}
                      </TableCell>
                      <TableCell>{ticket.description}</TableCell>
                      <TableCell>${ticket.price.toFixed(2)}</TableCell>
                      <TableCell>
                        {ticket.max_quantity || "Unlimited"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteTicket(ticket.ticket_name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
          <></>
        // <Card className="border-dashed border-gray-300 bg-gray-50">
        //   <CardContent className="flex flex-col items-center justify-center py-8">
        //     <p className="text-gray-500 mb-4">No ticket types added yet</p>
        //     <Button
        //       variant="outline"
        //       onClick={() => setIsAddDialogOpen(true)}
        //       type="button"
        //     >
        //       <TicketPlus className="h-4 w-4 mr-2" />
        //       Add Your First Ticket Type
        //     </Button>
        //   </CardContent>
        // </Card>
      )}
      {error && <p className="text-red-500 text-sm mt-1">{error} </p>}
    </div>
  );
};

export default TicketTypeManager;
